<template>
  <div v-if="activeCauldron" class="grid min-h-[72px] grid-cols-[minmax(0,1fr)_minmax(0,2fr)] items-stretch gap-2 border-b border-theme-border px-3 py-3" data-cy="admin-task-groups">
    <router-link
      :to="{ name: 'admin-home' }"
      class="flex min-h-[48px] items-center justify-center rounded-theme-md border border-theme-border bg-theme-surface dark:bg-theme-surface-raised text-theme-text-secondary shadow-sm transition-colors hover:bg-theme-surface-raised focus-visible:shadow-theme-focus"
      :aria-label="t('admin.workspace_navigation.dashboard')"
      :title="t('admin.workspace_navigation.dashboard')"
      @click="emit('navigate')"
    >
      <Icon :path="mdiHomeOutline" size="22" aria-hidden="true" />
    </router-link>
    <AppDropdown
      v-if="activeCauldron"
      v-model="workspaceId"
      class="workspace-picker min-w-0 flex-1"
      :data-workspace-id="activeCauldronId"
      :options="options"
      :placeholder="t('admin.workspace_navigation.workspace')"
      button-class="!h-auto min-h-[48px] !gap-1 !rounded-theme-md !px-2 !py-2 hover:bg-theme-surface-raised"
      options-class="!w-60 !min-w-0 right-0 !mt-3 !rounded-theme-lg !p-2 max-h-[calc(100dvh-9rem)] overflow-y-auto overscroll-contain"
      option-class="!min-h-[60px] !rounded-theme-md !px-2 !py-3"
      data-test="admin-workspace-switcher"
    >
      <template #trigger>
        <span class="workspace-trigger-icon flex h-7 w-7 shrink-0 items-center justify-center rounded-theme-md" :class="activeCauldron.accentClass">
          <Icon :path="activeCauldron.icon" size="18" aria-hidden="true" />
        </span>
        <span class="min-w-0 flex-1">
          <span class="block text-[10px] font-medium tracking-wide text-theme-text-muted">{{ t('admin.workspace_navigation.workspace') }}</span>
          <span class="block break-words text-xs font-semibold text-theme-text">{{ t(activeCauldron.labelKey) }}</span>
        </span>
        <Icon :path="mdiChevronDown" size="14" class="workspace-chevron !h-3.5 !w-3.5 shrink-0 text-theme-text-muted" aria-hidden="true" />
      </template>
      <template #option="{ option }">
        <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-theme-md" :class="option.accentClass" :data-workspace-option="option.value">
          <Icon :path="option.icon" size="18" aria-hidden="true" />
        </span>
        <span class="min-w-0">
          <span class="block text-sm font-semibold">{{ option.label }}</span>
          <span class="mt-1 block text-xs font-normal leading-relaxed text-theme-text-muted">{{ option.description }}</span>
        </span>
      </template>
    </AppDropdown>
  </div>
</template>

<script setup>
  /* eslint-disable no-undef */
  import { useAdminActiveCauldron } from '@/admin/composables/useAdminActiveCauldron';
  import AppDropdown from '@/components/ui/forms/AppDropdown.vue';
  import Icon from '@/components/Icon.vue';
  import { mdiChevronDown, mdiHomeOutline } from '@mdi/js';
  import { computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useRouter } from 'vue-router';

  const emit = defineEmits(['navigate']);
  const { t } = useI18n();
  const router = useRouter();
  const { cauldrons, activeCauldron, activeCauldronId } = useAdminActiveCauldron();
  const options = computed(() => cauldrons.map((workspace) => ({
    value: workspace.id,
    label: t(workspace.labelKey),
    description: t(workspace.descriptionKey),
    icon: workspace.icon,
    accentClass: workspace.accentClass,
  })));
  const workspaceId = computed({
    get: () => activeCauldronId.value || '',
    set: (id) => {
      const workspace = cauldrons.find((candidate) => candidate.id === id);
      if (workspace && id !== activeCauldronId.value) router.push(workspace.defaultRoute);
    },
  });
</script>

<style scoped>
  .workspace-picker :deep([role='listbox']) {
    transform-origin: top right;
    animation: workspace-picker-in var(--motion-duration-base) var(--motion-ease-emphasized);
  }
  .workspace-picker :deep(.v-leave-active),
  .workspace-chevron {
    transition-duration: var(--motion-duration-fast);
  }
  .workspace-chevron {
    transition-property: transform;
    transition-timing-function: var(--motion-ease-emphasized);
  }
  .workspace-picker :deep(button[aria-expanded='true']) .workspace-chevron {
    transform: rotate(180deg);
  }
  @keyframes workspace-picker-in {
    from { opacity: 0; transform: translateY(calc(-1 * var(--motion-travel-sm))); }
    to { opacity: 1; transform: translateY(0); }
  }
</style>
